import { EditorObject } from './editor-object';
import type { ObjectEvents } from './types/object-events';
import type { InteractiveObjectProps } from './types/interactive-props';
import { interactiveObjectDefaults } from './types/interactive-props';
import type { ControlRenderingStyleOverride } from './controls/control-rendering';
import { ControlSet, InteractiveControl, createDefaultControls } from './controls';
import {
  calcDimensionsMatrix,
  createRotateMatrix,
  createTranslateMatrix,
  degreesToRadians,
  IDENTITY_MATRIX,
  Matrix2D,
  multiplyMatrices,
  Point,
  qrDecompose,
  radiansToDegrees,
  sizeAfterTransform,
  ZERO,
} from '@imagise/shared';
import { TCornerPoint } from '../../object/types';

export type TOCoord = Point & {
  corner: TCornerPoint;
  touchCorner: TCornerPoint;
};

export interface ActiveControlInfo {
  key: string;
  control: InteractiveControl;
  coord: TOCoord;
}

type StyleOverride = ControlRenderingStyleOverride & {
  forActiveSelection?: boolean;
  hasBorders?: boolean;
  hasControls?: boolean;
};

/**
 * SSR-compatible interactive editor object
 */
export class InteractiveObject<EventSpec extends ObjectEvents = ObjectEvents>
  extends EditorObject<EventSpec>
  implements InteractiveObjectProps
{
  declare lockMovementX: boolean;
  declare lockMovementY: boolean;
  declare lockRotation: boolean;
  declare lockScalingX: boolean;
  declare lockScalingY: boolean;
  declare lockSkewingX: boolean;
  declare lockSkewingY: boolean;
  declare lockScalingFlip: boolean;

  declare cornerSize: number;
  declare touchCornerSize: number;
  declare transparentCorners: boolean;
  declare cornerColor: string;
  declare cornerStrokeColor: string;
  declare cornerStyle: 'rect' | 'circle';
  declare cornerDashArray: number[] | null;
  declare padding: number;
  declare hasControls: boolean;

  declare borderColor: string;
  declare borderDashArray: number[] | null;
  declare borderOpacityWhenMoving: number;
  declare borderScaleFactor: number;
  declare hasBorders: boolean;
  declare selectionBackgroundColor: string;

  declare selectable: boolean;
  declare evented: boolean;
  declare perPixelTargetFind: boolean;
  declare activeOn: 'down' | 'up';
  declare hoverCursor: CSSStyleDeclaration['cursor'] | null;
  declare moveCursor: CSSStyleDeclaration['cursor'] | null;
  declare snapAngle?: number;
  declare snapThreshold?: number;
  declare noScaleCache: boolean;

  controls: ControlSet = createDefaultControls();
  oCoords: Record<string, TOCoord> = {};
  isActive = false;
  isMoving = false;

  private _activeControlKey?: string;
  private _controlsVisibility: Record<string, boolean> = {};
  private _viewportTransform: Matrix2D = [...IDENTITY_MATRIX];
  private _retinaScaling = 1;

  constructor(options?: Partial<InteractiveObjectProps>) {
    super(options);
    this.initializeInteractiveDefaults();
    if (options) {
      this._setOptions(options);
    }
    this.setCoords();
  }

  private initializeInteractiveDefaults(): void {
    const self = this as unknown as Record<string, unknown>;
    Object.entries(interactiveObjectDefaults).forEach(([key, value]) => {
      if (typeof self[key] === 'undefined') {
        self[key] = value;
      }
    });
  }

  setViewportTransform(transform: Matrix2D): this {
    this._viewportTransform = [...transform];
    return this;
  }

  getViewportTransform(): Matrix2D {
    return this._viewportTransform;
  }

  setRetinaScaling(value: number): this {
    this._retinaScaling = value || 1;
    return this;
  }

  protected getCanvasRetinaScaling(): number {
    return this._retinaScaling;
  }

  protected getViewportZoom(): number {
    const [a, b] = this._viewportTransform;
    const zoom = Math.sqrt(a * a + b * b);
    return zoom || 1;
  }

  getTotalAngle(): number {
    const matrix = this.calcTransformMatrix();
    return radiansToDegrees(Math.atan2(matrix[1], matrix[0]));
  }

  getActiveControl(): ActiveControlInfo | undefined {
    const key = this._activeControlKey;
    if (!key) {
      return undefined;
    }
    const control = this.controls[key];
    const coord = this.oCoords[key];
    if (!control || !coord) {
      return undefined;
    }
    return { key, control, coord };
  }

  findControl(pointer: Point, forTouch = false): ActiveControlInfo | undefined {
    if (!this.hasControls || !this.isActive) {
      return undefined;
    }
    this._activeControlKey = undefined;
    const entries = Object.entries(this.oCoords);
    for (let i = entries.length - 1; i >= 0; i--) {
      const [key, coord] = entries[i];
      const control = this.controls[key];
      if (!control) {
        continue;
      }
      const hitArea = forTouch ? coord.touchCorner : coord.corner;
      if (control.shouldActivate(key, this, pointer, hitArea)) {
        this._activeControlKey = key;
        return { key, control, coord };
      }
    }
    return undefined;
  }

  calcOCoords(): Record<string, TOCoord> {
    const vpt = this.getViewportTransform();
    const center = this.getCenterPoint();
    const translate = createTranslateMatrix(center.x, center.y);
    const rotation = createRotateMatrix(this.getTotalAngle() - (this.group && this.flipX ? 180 : 0));
    const positionMatrix = multiplyMatrices(translate, rotation);
    const startMatrix = multiplyMatrices(vpt, positionMatrix);
    const scaleX = vpt[0] || 1;
    const scaleY = vpt[3] || 1;
    const finalMatrix = multiplyMatrices(startMatrix, [1 / scaleX, 0, 0, 1 / scaleY, 0, 0]);
    const transformOptions = this.group ? qrDecompose(this.calcTransformMatrix()) : undefined;
    if (transformOptions) {
      transformOptions.scaleX = Math.abs(transformOptions.scaleX);
      transformOptions.scaleY = Math.abs(transformOptions.scaleY);
    }

    const dim = this._getTransformedDimensions(transformOptions);
    const coords: Record<string, TOCoord> = {};

    this.forEachControl((control, key) => {
      const position = control.positionHandler(dim, finalMatrix);
      coords[key] = Object.assign(position, this.calcControlCornerCoords(control, position));
    });

    return coords;
  }

  private calcControlCornerCoords(control: InteractiveControl, position: Point) {
    const angle = this.getTotalAngle();
    return {
      corner: control.calcCornerCoords(angle, this.cornerSize, position.x, position.y, false, this),
      touchCorner: control.calcCornerCoords(angle, this.touchCornerSize, position.x, position.y, true, this),
    };
  }

  override setCoords(): this {
    super.setCoords();
    this.oCoords = this.calcOCoords();
    return this;
  }

  forEachControl(iterator: (control: InteractiveControl, key: string, object: this) => void): void {
    Object.keys(this.controls).forEach(key => {
      const control = this.controls[key];
      if (control) {
        iterator(control, key, this);
      }
    });
  }

  drawSelectionBackground(ctx?: CanvasRenderingContext2D | null): void {
    if (!ctx || !this.selectionBackgroundColor || !this.isActive) {
      return;
    }
    ctx.save();
    const center = this.getCenterPoint();
    const dim = this._getTransformedDimensions();
    const vpt = this.getViewportTransform();
    ctx.translate(center.x, center.y);
    ctx.scale(1 / vpt[0], 1 / vpt[3]);
    ctx.rotate(degreesToRadians(this.angle));
    ctx.fillStyle = this.selectionBackgroundColor;
    ctx.fillRect(-dim.x / 2, -dim.y / 2, dim.x, dim.y);
    ctx.restore();
  }

  strokeBorders(ctx: CanvasRenderingContext2D, size: Point): void {
    ctx.strokeRect(-size.x / 2, -size.y / 2, size.x, size.y);
  }

  private drawBordersInternal(ctx: CanvasRenderingContext2D, size: Point, styleOverride: StyleOverride = {}): void {
    const options = {
      hasControls: this.hasControls,
      borderColor: this.borderColor,
      borderDashArray: this.borderDashArray,
      ...styleOverride,
    };
    ctx.save();
    ctx.strokeStyle = options.borderColor || this.borderColor;
    this.setLineDash(ctx, options.borderDashArray);
    this.strokeBorders(ctx, size);
    if (options.hasControls) {
      this.drawControlsConnectingLines(ctx, size);
    }
    ctx.restore();
  }

  _renderControls(ctx: CanvasRenderingContext2D, styleOverride: StyleOverride = {}): void {
    const shouldDrawBorders = styleOverride.hasBorders ?? this.hasBorders;
    const shouldDrawControls = styleOverride.hasControls ?? this.hasControls;
    if (!shouldDrawBorders && !shouldDrawControls) {
      return;
    }

    const vpt = this.getViewportTransform();
    const matrix = multiplyMatrices(vpt, this.calcTransformMatrix());
    const options = qrDecompose(matrix);

    ctx.save();
    ctx.translate(options.translateX, options.translateY);
    ctx.lineWidth = this.borderScaleFactor;
    if (this.isMoving) {
      ctx.globalAlpha = this.borderOpacityWhenMoving;
    }
    if (this.flipX) {
      options.angle -= 180;
    }
    ctx.rotate(degreesToRadians(this.group ? options.angle : this.angle));
    if (shouldDrawBorders) {
      this.drawBorders(ctx, options, styleOverride);
    }
    if (shouldDrawControls) {
      this.drawControls(ctx, styleOverride);
    }
    ctx.restore();
  }

  drawBorders(
    ctx: CanvasRenderingContext2D,
    options: ReturnType<typeof qrDecompose>,
    styleOverride: StyleOverride
  ): void {
    let size: Point;
    if (styleOverride.forActiveSelection || this.group) {
      const bbox = sizeAfterTransform(this.width, this.height, calcDimensionsMatrix(options));
      const stroke = !this.isStrokeAccountedForInDimensions()
        ? (this.strokeUniform
            ? new Point(this.getViewportZoom(), this.getViewportZoom())
            : new Point(options.scaleX, options.scaleY)
          ).scalarMultiply(this.strokeWidth)
        : ZERO;
      size = bbox
        .add(stroke)
        .scalarAdd(this.borderScaleFactor)
        .scalarAdd(this.padding * 2);
    } else {
      // The border is stroked in screen space: size it from the decomposed
      // screen-space scale (object scale × viewport zoom), otherwise a
      // zoomed-out fit draws it larger than the object itself.
      size = this._getTransformedDimensions({
        scaleX: Math.abs(options.scaleX),
        scaleY: Math.abs(options.scaleY),
      }).scalarAdd(this.borderScaleFactor);
    }
    this.drawBordersInternal(ctx, size, styleOverride);
  }

  drawControlsConnectingLines(ctx: CanvasRenderingContext2D, size: Point): void {
    let shouldStroke = false;
    ctx.beginPath();
    this.forEachControl((control, key) => {
      if (control.withConnection && this.isControlVisible(key)) {
        shouldStroke = true;
        ctx.moveTo(control.x * size.x, control.y * size.y);
        ctx.lineTo(control.x * size.x + control.offsetX, control.y * size.y + control.offsetY);
      }
    });
    if (shouldStroke) {
      ctx.stroke();
    }
  }

  drawControls(ctx: CanvasRenderingContext2D, styleOverride: ControlRenderingStyleOverride = {}): void {
    ctx.save();
    const retinaScaling = this.getCanvasRetinaScaling();
    ctx.setTransform(retinaScaling, 0, 0, retinaScaling, 0, 0);
    const options = {
      cornerStrokeColor: this.cornerStrokeColor,
      cornerDashArray: this.cornerDashArray,
      cornerColor: this.cornerColor,
      transparentCorners: this.transparentCorners,
      ...styleOverride,
    };
    ctx.strokeStyle = ctx.fillStyle = options.cornerColor || this.cornerColor;
    if (!options.transparentCorners) {
      ctx.strokeStyle = options.cornerStrokeColor || this.cornerStrokeColor;
    }
    this.setLineDash(ctx, options.cornerDashArray || null);
    this.forEachControl((control, key) => {
      if (this.isControlVisible(key)) {
        const coord = this.oCoords[key];
        control.render(ctx, coord.x, coord.y, options, this);
      }
    });
    ctx.restore();
  }

  private setLineDash(ctx: CanvasRenderingContext2D, dashArray?: number[] | null): void {
    if (!ctx.setLineDash) {
      return;
    }
    ctx.setLineDash(dashArray || []);
  }

  isControlVisible(controlKey: string): boolean {
    if (typeof this._controlsVisibility[controlKey] === 'boolean') {
      return this._controlsVisibility[controlKey];
    }
    return this.controls[controlKey]?.visible ?? false;
  }

  setControlVisible(controlKey: string, visible: boolean): void {
    this._controlsVisibility[controlKey] = visible;
  }

  setControlsVisibility(options: Record<string, boolean> = {}): void {
    Object.entries(options).forEach(([key, value]) => this.setControlVisible(key, value));
  }

  onDeselect(): boolean {
    return false;
  }

  onSelect(): boolean {
    return false;
  }

  shouldStartDragging(): boolean {
    return false;
  }

  onDragStart(): boolean {
    return false;
  }

  canDrop(): boolean {
    return false;
  }

  renderDragSourceEffect(): void {
    // noop
  }

  renderDropTargetEffect(): void {
    // noop
  }

  protected isStrokeAccountedForInDimensions(): boolean {
    return false;
  }
}
